import { cn } from "@app/lib/utils";
import { URLS } from "@app/shared";
import { Link } from "@design-system/components";
import { pillars, type Pillar, type PillarIcon } from "@modules/investor-relations/data";
import { trans } from "@mongez/localization";
import { useLocation } from "@remix-run/react";
import {
  ArrowRight,
  ChevronDown,
  ChevronRight,
  Globe,
  Layers,
  ShieldCheck,
  Target,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useState } from "react";

const icons: Record<PillarIcon, LucideIcon> = {
  sectors: Layers,
  globe: Globe,
  governance: ShieldCheck,
  vision: Target,
};

function pillarNumber(index: number) {
  return String(index + 1).padStart(2, "0");
}

type PillarCardProps = {
  pillar: Pillar;
  index: number;
  isActive: boolean;
  isDetailOpen: boolean;
  onSelect: () => void;
  onToggleDetail: () => void;
  onNext: () => void;
};

/**
 * One card shape for both states. Keeping the element mounted across the
 * active/inactive switch is what makes the change animate — the photo layer
 * cross-fades, the card's flex basis widens and the palette transitions,
 * instead of a different element being swapped in.
 */
function PillarCard({
  pillar,
  index,
  isActive,
  isDetailOpen,
  onSelect,
  onToggleDetail,
  onNext,
}: PillarCardProps) {
  const { key, icon, image, imagePosition, hasDetail } = pillar;
  const Icon = icons[icon];
  const title = trans(`investmentCase.${key}.title`);

  return (
    <article
      className={cn(
        "group relative flex min-h-[19rem] flex-col overflow-hidden rounded-2xl border p-5 transition-[flex-grow,flex-basis,border-color,box-shadow,transform,padding] duration-500 lg:p-6",
        isActive
          ? "border-bright-orange/40 shadow-xl sm:col-span-2 lg:flex-[2.2] lg:p-8"
          : "border-mercury bg-white shadow-sm hover:-translate-y-1 hover:border-secondary/30 hover:shadow-xl lg:flex-1",
      )}
    >
      {/* Photo layer — always mounted so promoting a pillar cross-fades. */}
      <div
        className={cn(
          "absolute inset-0 transition-opacity duration-500",
          isActive ? "opacity-100" : "opacity-0",
        )}
        aria-hidden
      >
        <img
          src={image}
          alt=""
          style={{ objectPosition: imagePosition }}
          className="h-full w-full object-cover"
          loading="lazy"
        />
        <span className="absolute inset-0 bg-gradient-to-t from-marine via-marine/80 to-marine/25" />
      </div>

      <div className="relative z-10 flex flex-1 flex-col gap-3">
        <span className="flex items-start justify-between gap-3">
          <span
            className={cn(
              "font-bold leading-none transition-all duration-500",
              isActive
                ? "text-[3.5rem] text-bright-orange"
                : "text-4xl text-secondary/45",
            )}
          >
            {pillarNumber(index)}
          </span>
          <Icon
            className={cn(
              "size-6 shrink-0 text-secondary/70 transition-opacity duration-500",
              isActive && "opacity-0",
            )}
            strokeWidth={1.5}
            aria-hidden
          />
        </span>

        <h3
          className={cn(
            "font-bold leading-snug transition-colors duration-500",
            isActive
              ? "max-w-md text-xl text-white sm:text-2xl"
              : "text-base text-secondary",
          )}
        >
          {title}
        </h3>

        <p
          className={cn(
            "text-sm leading-relaxed transition-colors duration-500",
            isActive
              ? "max-w-md text-white/75"
              : "line-clamp-6 text-marine/65",
          )}
        >
          {trans(`investmentCase.${key}.summary`)}
        </p>

        {isActive && hasDetail && (
          <>
            {/* Height animates via the 0fr → 1fr grid row, no measuring. */}
            <div
              className={cn(
                "grid transition-[grid-template-rows] duration-500",
                isDetailOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
              )}
            >
              <p className="max-w-md overflow-hidden text-sm leading-relaxed text-white/75">
                {trans(`investmentCase.${key}.detail`)}
              </p>
            </div>

            <button
              type="button"
              onClick={onToggleDetail}
              aria-expanded={isDetailOpen}
              className="mt-auto inline-flex w-max items-center gap-2 pt-2 text-sm font-bold text-bright-orange transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              {trans(isDetailOpen ? "common.readLess" : "common.readMore")}
              <ChevronDown
                className={cn(
                  "size-4 transition-transform duration-300",
                  isDetailOpen && "rotate-180",
                )}
                strokeWidth={2}
                aria-hidden
              />
            </button>
          </>
        )}

        {!isActive && (
          <ArrowRight
            className="mt-auto size-5 self-end text-marine/35 transition-colors group-hover:text-bright-orange rtl:rotate-180"
            strokeWidth={1.8}
            aria-hidden
          />
        )}
      </div>

      {isActive ? (
        <button
          type="button"
          onClick={onNext}
          aria-label={trans("investmentCase.nextPillar")}
          className="absolute end-5 top-1/2 z-20 grid size-11 -translate-y-1/2 place-items-center rounded-full bg-bright-orange text-white shadow-lg transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white lg:end-6"
        >
          <ArrowRight
            className="size-5 rtl:rotate-180"
            strokeWidth={2.2}
            aria-hidden
          />
        </button>
      ) : (
        // Full-card promote target. Kept as a sibling overlay rather than a
        // wrapping button so the active card's own controls are never nested
        // inside another button.
        <button
          type="button"
          onClick={onSelect}
          aria-label={trans("investmentCase.showPillar", { pillar: title })}
          className="absolute inset-0 z-20 rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary"
        />
      )}
    </article>
  );
}

export function InvestmentCase() {
  const { pathname } = useLocation();
  const isInvestmentCasePage = pathname.includes(URLS.investor.investmentCase);
  const [activeKey, setActiveKey] = useState(pillars[0].key);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  const activeIndex = pillars.findIndex(({ key }) => key === activeKey);

  function selectPillar(key: Pillar["key"]) {
    setActiveKey(key);
    setIsDetailOpen(false);
  }

  function showNextPillar() {
    selectPillar(pillars[(activeIndex + 1) % pillars.length].key);
  }

  return (
    <section className="bg-white py-16 sm:py-20">
      <div className="container flex flex-col gap-10 sm:gap-12">
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div className="flex max-w-5xl flex-col gap-5">
            <div className="flex items-center gap-3">
              <span className="h-px w-8 bg-secondary" />
              <span className="text-sm font-semibold uppercase tracking-widest text-secondary">
                {trans("investmentCase.title")}
              </span>
            </div>
            <h2 className="max-w-2xl text-4xl font-bold leading-tight text-marine sm:text-5xl xl:text-[3.25rem]">
              {trans("investmentCase.tagline")}
            </h2>
            <p className="max-w-full text-base leading-relaxed text-marine/75 sm:text-xl">
              {trans("investmentCase.intro")}
            </p>
          </div>
          {!isInvestmentCasePage && (
            <Link
              to={URLS.investor.investmentCase}
              className="flex shrink-0 items-center gap-2 text-sm font-bold text-secondary transition-colors hover:text-marine"
            >
              {trans("viewMore")}
              <ChevronRight
                className="h-4 w-4 rtl:rotate-180"
                strokeWidth={1.5}
                aria-hidden
              />
            </Link>
          )}
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:flex lg:items-stretch">
          {pillars.map((pillar, index) => (
            <PillarCard
              key={pillar.key}
              pillar={pillar}
              index={index}
              isActive={pillar.key === activeKey}
              isDetailOpen={isDetailOpen}
              onSelect={() => selectPillar(pillar.key)}
              onToggleDetail={() => setIsDetailOpen((open) => !open)}
              onNext={showNextPillar}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
